import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-scroll-navigation',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './scroll-navigation.component.html',
  styleUrls: ['./scroll-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ScrollNavigationComponent implements AfterViewInit, OnDestroy {
  showScrollToTop = false;
  showScrollToBottom = false;

  private scrollContainers: HTMLElement[] = [];
  private removeScrollListeners: Array<() => void> = [];
  private removeResizeListener?: () => void;
  private navigationSubscription?: Subscription;
  private contentMutationObserver?: MutationObserver;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.scrollContainers = this.findScrollContainers();
    this.removeScrollListeners = this.scrollContainers.map((scrollContainer) =>
      this.renderer.listen(scrollContainer, 'scroll', () => this.refreshVisibility()),
    );
    this.removeScrollListeners.push(
      this.renderer.listen('window', 'scroll', () => this.refreshVisibility()),
    );
    this.removeResizeListener = this.renderer.listen('window', 'resize', () => {
      this.refreshVisibility();
    });
    const contentContainer = this.scrollContainers[0];
    if (contentContainer) {
      this.contentMutationObserver = new MutationObserver(() => this.refreshVisibility());
      this.contentMutationObserver.observe(contentContainer, {
        childList: true,
        subtree: true,
      });
    }
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.showScrollToTop = false;
        this.showScrollToBottom = false;
        setTimeout(() => this.refreshVisibility());
      });

    this.refreshVisibility();
  }

  ngOnDestroy(): void {
    this.removeScrollListeners.forEach((removeListener) => removeListener());
    this.removeResizeListener?.();
    this.navigationSubscription?.unsubscribe();
    this.contentMutationObserver?.disconnect();
  }

  scrollToTop(event: Event): void {
    event.stopPropagation();
    if (!this.scrollContainers.length) return;

    this.scrollContainers.forEach((scrollContainer) => this.scrollContainerTo(scrollContainer, 0));
    this.showScrollToTop = false;
  }

  scrollToBottom(event: Event): void {
    event.stopPropagation();
    if (!this.scrollContainers.length) return;

    this.scrollContainers.forEach((scrollContainer) =>
      this.scrollContainerTo(scrollContainer, scrollContainer.scrollHeight),
    );
    this.showScrollToBottom = false;
  }

  private refreshVisibility(): void {
    this.updateVisibility();
    this.changeDetectorRef.detectChanges();
  }

  private updateVisibility(): void {
    const scrollableContainers = this.scrollContainers.filter(
      (scrollContainer) => scrollContainer.scrollHeight > scrollContainer.clientHeight + 1,
    );
    this.showScrollToTop = scrollableContainers.some((scrollContainer) => scrollContainer.scrollTop > 0);
    this.showScrollToBottom = scrollableContainers.some(
      (scrollContainer) =>
        scrollContainer.scrollTop <
        scrollContainer.scrollHeight - scrollContainer.clientHeight - 1,
    );
  }

  private isDocumentScroll(scrollContainer: HTMLElement): boolean {
    return scrollContainer === document.scrollingElement;
  }

  private findScrollContainers(): HTMLElement[] {
    const scrollContainers: HTMLElement[] = [];
    let current = this.elementRef.nativeElement.parentElement;
    while (current) {
      const style = getComputedStyle(current);
      if (/(auto|scroll|overlay)/.test(style.overflowY)) scrollContainers.push(current);
      current = current.parentElement;
    }

    const documentScroll = document.scrollingElement as HTMLElement;
    if (documentScroll && !scrollContainers.includes(documentScroll)) {
      scrollContainers.push(documentScroll);
    }
    return scrollContainers;
  }

  private scrollContainerTo(scrollContainer: HTMLElement, top: number): void {
    if (this.isDocumentScroll(scrollContainer)) {
      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      scrollContainer.scrollTo({ top, behavior: 'smooth' });
    }
  }
}
